import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Trash2, KeyRound, Search, Beaker, Copy, Check, UserPlus, Zap, Mail, RotateCw, Loader2 } from "lucide-react";

const ROLE_BADGE = {
  admin: "bg-[#0F172A] text-white",
  company: "bg-[#0025D8] text-white",
  candidate: "bg-secondary text-foreground",
};

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString(); } catch { return d; }
}

export default function AdminUsers({ defaultRole, pageTitle }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState(defaultRole || "all");
  const [planFilter, setPlanFilter] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // Reset password dialog state
  const [resetTarget, setResetTarget] = useState(null);
  const [resetMode, setResetMode] = useState("generate"); // generate | manual
  const [manualPassword, setManualPassword] = useState("");
  const [resetResult, setResetResult] = useState(null);
  const [copied, setCopied] = useState(false);

  // Delete dialog state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Manage application email (managed inbox) dialog state
  const [emailTarget, setEmailTarget] = useState(null);   // the candidate
  const [inbox, setInbox] = useState(null);               // {address, real_email, aliases, accounts, domain}
  const [inboxLoading, setInboxLoading] = useState(false);
  const [customLocal, setCustomLocal] = useState("");
  const [emailBusy, setEmailBusy] = useState("");         // "" | "rotate" | "save" | tenant key

  // Autopilot usage, per candidate. Opened from the row; the dispute it exists to settle is
  // almost never about the total but about WHICH applications counted, so the dialog shows the
  // arithmetic and the list rather than a number.
  const [usageTarget, setUsageTarget] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [grantAmount, setGrantAmount] = useState("");
  const [grantReason, setGrantReason] = useState("");
  const [granting, setGranting] = useState(false);

  const loadUsage = async (userId) => {
    setUsageLoading(true);
    try {
      const { data } = await api.get(`/admin/apply/usage/${userId}`);
      setUsage(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Autopilot usage");
      setUsage(null);
    } finally { setUsageLoading(false); }
  };

  const openUsage = async (u) => {
    setUsageTarget(u); setUsage(null); setGrantAmount(""); setGrantReason("");
    await loadUsage(u.user_id);
  };

  const submitGrant = async () => {
    const n = parseInt(grantAmount, 10);
    if (!n) { toast.error("Enter a number of applications (negative to take back)"); return; }
    setGranting(true);
    try {
      await api.post(`/admin/apply/usage/${usageTarget.user_id}/grant`,
                     { amount: n, reason: grantReason });
      toast.success(`${n > 0 ? "Added" : "Removed"} ${Math.abs(n)} application${Math.abs(n) === 1 ? "" : "s"}`);
      setGrantAmount(""); setGrantReason("");
      await loadUsage(usageTarget.user_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply the grant");
    } finally { setGranting(false); }
  };

  const openEmail = async (u) => {
    setEmailTarget(u); setInbox(null); setCustomLocal(""); setInboxLoading(true);
    try {
      const { data } = await api.get(`/admin/apply/candidate-inbox/${u.user_id}`);
      setInbox(data);
    } catch { toast.error("Could not load this candidate's inbox"); }
    finally { setInboxLoading(false); }
  };
  const refreshInbox = async () => {
    if (!emailTarget) return;
    const { data } = await api.get(`/admin/apply/candidate-inbox/${emailTarget.user_id}`);
    setInbox(data);
  };
  const changeEmail = async (body, kind) => {
    setEmailBusy(kind);
    try {
      const { data } = await api.post("/admin/apply/managed-inbox", { user_id: emailTarget.user_id, ...body });
      toast.success(`Application email changed to ${data.address}`);
      setCustomLocal(""); await refreshInbox();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change the email"); }
    finally { setEmailBusy(""); }
  };
  const resetAccount = async (a) => {
    if (!window.confirm(`Reset the ${a.company || a.tenant} account? The next apply there will create a fresh account under the current email.`)) return;
    setEmailBusy(a.tenant);
    try {
      await api.post("/admin/apply/reset-ats-account", { user_id: emailTarget.user_id, ats: a.ats, tenant: a.tenant });
      toast.success(`Reset ${a.company || a.tenant}`); await refreshInbox();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reset the account"); }
    finally { setEmailBusy(""); }
  };

  // Create user dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createRole, setCreateRole] = useState("candidate");
  const [createPassword, setCreatePassword] = useState("");
  const [createResult, setCreateResult] = useState(null);
  const [createCopied, setCreateCopied] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: sortField, order: sortOrder };
      if (q.trim()) params.q = q.trim();
      if (roleFilter !== "all") params.role = roleFilter;
      if (planFilter !== "all") params.plan = planFilter;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
    } catch (e) {
      toast.error("Could not load users");
    } finally { setLoading(false); }
  }, [sortField, sortOrder, roleFilter, planFilter, q]);

  const togglePlan = async (u) => {
    const newPlan = u.plan === "premium" ? "basic" : "premium";
    setBusy(true);
    setUsers((arr) => arr.map((x) => x.user_id === u.user_id ? { ...x, plan: newPlan } : x));
    try {
      await api.patch(`/admin/users/${u.user_id}/plan`, { plan: newPlan });
      toast.success(`${u.email} → ${newPlan}`);
    } catch (e) {
      setUsers((arr) => arr.map((x) => x.user_id === u.user_id ? { ...x, plan: u.plan } : x));
      toast.error(e?.response?.data?.detail || "Could not update plan");
    } finally { setBusy(false); }
  };

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const onSearch = (e) => { e.preventDefault(); fetchUsers(); };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateName("");
    setCreateEmail("");
    setCreateRole("candidate");
    setCreatePassword("");
    setCreateResult(null);
    setCreateCopied(false);
  };

  const performCreate = async () => {
    if (!createName.trim() || !createEmail.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (createPassword && createPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const payload = { name: createName.trim(), email: createEmail.trim(), role: createRole };
      if (createPassword) payload.password = createPassword;
      const { data } = await api.post("/admin/users", payload);
      setCreateResult(data);
      setUsers((arr) => [data.user, ...arr]);
      toast.success(`User ${data.user.email} created`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create user");
    } finally { setBusy(false); }
  };

  const copyCreatePassword = async () => {
    if (!createResult?.temporary_password) return;
    try {
      await navigator.clipboard.writeText(createResult.temporary_password);
      setCreateCopied(true);
      setTimeout(() => setCreateCopied(false), 1800);
    } catch {}
  };

  const stats = useMemo(() => ({
    total: users.length,
    candidates: users.filter((u) => u.role === "candidate").length,
    companies: users.filter((u) => u.role === "company").length,
    admins: users.filter((u) => u.role === "admin").length,
    premium: users.filter((u) => u.role === "candidate" && u.plan === "premium").length,
  }), [users]);

  const changeRole = async (u, newRole) => {
    if (newRole === u.role) return;
    const prevRole = u.role;
    setBusy(true);
    setUsers((arr) => arr.map((x) => x.user_id === u.user_id ? { ...x, role: newRole } : x));
    try {
      await api.patch(`/admin/users/${u.user_id}/role`, { role: newRole });
      toast.success(`${u.email} → ${newRole}`);
    } catch (e) {
      setUsers((arr) => arr.map((x) => x.user_id === u.user_id ? { ...x, role: prevRole } : x));
      toast.error(e?.response?.data?.detail || "Could not change role");
    } finally { setBusy(false); }
  };

  const toggleTestFlag = async (u) => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.user_id}/test-flag`, { is_test: !u.is_test });
      toast.success(`${u.email} → ${!u.is_test ? "Test mode ON" : "Test mode OFF"}`);
      setUsers((arr) => arr.map((x) => x.user_id === u.user_id ? { ...x, is_test: !u.is_test } : x));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update flag");
    } finally { setBusy(false); }
  };

  const toggleSuperuser = async (u) => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${u.user_id}/superuser`, { is_superuser: !u.is_superuser });
      toast.success(`${u.email} → superuser ${!u.is_superuser ? "ON" : "OFF"}`);
      setUsers((arr) => arr.map((x) => x.user_id === u.user_id ? { ...x, is_superuser: !u.is_superuser } : x));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update superuser flag");
    } finally { setBusy(false); }
  };

  const performReset = async () => {
    if (!resetTarget) return;
    if (resetMode === "manual" && manualPassword.trim().length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const payload = resetMode === "manual" ? { password: manualPassword } : {};
      const { data } = await api.post(`/admin/users/${resetTarget.user_id}/reset-password`, payload);
      setResetResult(data);
      toast.success("Password reset successfully");
      setManualPassword("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  const closeReset = () => {
    setResetTarget(null);
    setResetResult(null);
    setManualPassword("");
    setResetMode("generate");
    setCopied(false);
  };

  const copyTempPassword = async () => {
    if (!resetResult?.temporary_password) return;
    try {
      await navigator.clipboard.writeText(resetResult.temporary_password);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  const performDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText !== deleteTarget.email) {
      toast.error("Type the user's email exactly to confirm.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${deleteTarget.user_id}`);
      toast.success(`Deleted ${data.deleted.email} (${data.deleted.interviews} interviews, ${data.deleted.jobs} jobs)`);
      setUsers((arr) => arr.filter((u) => u.user_id !== deleteTarget.user_id));
      setDeleteTarget(null);
      setDeleteConfirmText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <Link to="/admin" className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-[#0025D8] inline-flex items-center gap-1.5 transition-colors group">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-0.5 transition-transform" /> Admin
          </Link>
          <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mt-2">{pageTitle || "User management"}</h1>
          <p className="text-muted-foreground mt-1">List, search, reset passwords, delete accounts.</p>
        </div>
        <div className="flex items-start gap-4 flex-wrap">
          <Button
            onClick={() => setCreateOpen(true)}
            className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3] h-12 px-5"
            data-testid="add-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" /> Add User
          </Button>
          <div className="flex gap-px border-l border-t border-border">
            {[
              { l: "TOTAL", v: stats.total },
              { l: "CANDIDATES", v: stats.candidates },
              { l: "PREMIUM", v: stats.premium },
              { l: "COMPANIES", v: stats.companies },
              { l: "ADMINS", v: stats.admins },
            ].map((s) => (
              <div key={s.l} className="bg-white border-r border-b border-border px-5 py-3">
                <div className="font-mono text-[10px] text-muted-foreground tracking-[0.25em]">{s.l}</div>
                <div className="font-display text-2xl font-bold">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={onSearch} className="flex flex-wrap gap-3 mb-6 items-end">
        <div className="flex-1 min-w-[260px]">
          <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name or email…"
              className="rounded-xl pl-10 py-6"
              data-testid="user-search"
            />
          </div>
        </div>
        <div>
          <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Role</Label>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="rounded-xl w-40 h-12" data-testid="role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All roles</SelectItem>
              <SelectItem value="candidate">Candidate</SelectItem>
              <SelectItem value="company">Company</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Plan</Label>
          <Select value={planFilter} onValueChange={setPlanFilter}>
            <SelectTrigger className="rounded-xl w-36 h-12" data-testid="plan-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All plans</SelectItem>
              <SelectItem value="basic">Free (Basic)</SelectItem>
              <SelectItem value="premium">Premium</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Sort by</Label>
          <Select value={sortField} onValueChange={setSortField}>
            <SelectTrigger className="rounded-xl w-44 h-12" data-testid="sort-field">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Created</SelectItem>
              <SelectItem value="last_login_at">Last login</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="role">Role</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Order</Label>
          <Select value={sortOrder} onValueChange={setSortOrder}>
            <SelectTrigger className="rounded-xl w-32 h-12" data-testid="sort-order">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">↓ Desc</SelectItem>
              <SelectItem value="asc">↑ Asc</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3] h-12 px-6" data-testid="apply-filters">Apply</Button>
      </form>

      {/* Table */}
      {loading ? (
        <div className="p-12 font-mono text-sm text-muted-foreground">Loading users…</div>
      ) : users.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground">No users match the current filters.</div>
      ) : (
        <div className="border-l border-t border-border overflow-x-auto">
          <div className="min-w-[1200px]">
            <div className="grid grid-cols-12 border-r border-b border-border bg-secondary/40 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <div className="col-span-3 p-3 border-r border-border">User</div>
              <div className="col-span-1 p-3 border-r border-border">Role</div>
              <div className="col-span-1 p-3 border-r border-border">Test</div>
              <div className="col-span-2 p-3 border-r border-border">Plan · Usage</div>
              <div className="col-span-1 p-3 border-r border-border">Created</div>
              <div className="col-span-1 p-3 border-r border-border">Last login</div>
              <div className="col-span-1 p-3 border-r border-border">Total</div>
              <div className="col-span-2 p-3">Actions</div>
            </div>
            {users.map((u) => (
              <div key={u.user_id} className="grid grid-cols-12 border-r border-b border-border items-center hover:bg-secondary/20">
                {/* User */}
                <div className="col-span-3 p-4 border-r border-border min-w-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-[#0025D8] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {u.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{u.name}{u.is_self && <span className="ml-2 font-mono text-[10px] text-muted-foreground">YOU</span>}</div>
                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      <div className="font-mono text-[10px] text-muted-foreground truncate">{u.user_id}</div>
                    </div>
                  </div>
                </div>
                {/* Role */}
                <div className="col-span-1 p-4 border-r border-border">
                  <span className={`inline-block px-2 py-1 text-xs font-semibold uppercase tracking-wider ${ROLE_BADGE[u.role] || "bg-secondary"}`}>{u.role}</span>
                </div>
                {/* Test flag */}
                <div className="col-span-1 p-4 border-r border-border">
                  {u.role === "candidate" ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleTestFlag(u)}
                      disabled={busy}
                      className={`rounded-xl h-8 px-2 text-xs ${u.is_test ? "bg-[#F59E0B] text-white border-[#F59E0B] hover:bg-[#D97706]" : ""}`}
                      data-testid={`test-flag-${u.user_id}`}
                    >
                      <Beaker className="w-3 h-3 mr-1" /> {u.is_test ? "ON" : "OFF"}
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </div>
                {/* Plan · Usage */}
                <div className="col-span-2 p-4 border-r border-border">
                  {u.role === "candidate" ? (
                    <button
                      onClick={() => togglePlan(u)}
                      disabled={busy}
                      title="Click to toggle plan"
                      className="flex flex-col gap-1 group"
                    >
                      {u.plan === "premium" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider bg-[#7C3AED] text-white group-hover:bg-[#6D28D9] transition-colors">
                          <Zap className="w-2.5 h-2.5" /> Premium
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider rounded-xl border border-border text-muted-foreground group-hover:border-[#7C3AED] group-hover:text-[#7C3AED] transition-colors">
                          Free
                        </span>
                      )}
                      {u.plan !== "premium" && (
                        <span className="font-mono text-[10px] text-muted-foreground">
                          {u.interviews_this_month ?? 0} / 2 this month
                        </span>
                      )}
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </div>
                {/* Created */}
                <div className="col-span-1 p-4 border-r border-border font-mono text-xs text-muted-foreground">{fmt(u.created_at)}</div>
                {/* Last login */}
                <div className="col-span-1 p-4 border-r border-border font-mono text-xs text-muted-foreground">{fmt(u.last_login_at)}</div>
                {/* Total interviews */}
                <div className="col-span-1 p-4 border-r border-border font-mono text-xs">{u.interview_count ?? 0}</div>
                {/* Actions */}
                <div className="col-span-2 p-4 flex gap-1.5 flex-wrap items-center">
                  <Select
                    value={u.role}
                    onValueChange={(newRole) => changeRole(u, newRole)}
                    disabled={u.is_self || busy}
                  >
                    <SelectTrigger className="rounded-xl h-8 text-xs w-24" data-testid={`role-select-${u.user_id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="candidate">Candidate</SelectItem>
                      <SelectItem value="company">Company</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setResetTarget(u); setResetResult(null); setResetMode("generate"); }}
                    className="rounded-xl h-8 px-2 text-xs"
                    data-testid={`reset-${u.user_id}`}
                  >
                    <KeyRound className="w-3 h-3" />
                  </Button>
                  {me?.is_superuser && u.role === "admin" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleSuperuser(u)}
                      disabled={busy}
                      className={`rounded-xl h-8 px-2 text-xs ${u.is_superuser ? "bg-[#7C3AED] text-white border-[#7C3AED] hover:bg-[#6D28D9]" : ""}`}
                      title="Toggle superuser access"
                    >
                      ⚡
                    </Button>
                  )}
                  {u.role === "candidate" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openUsage(u)}
                      className="rounded-xl h-8 px-2 text-xs"
                      title="Autopilot applications — usage and grants"
                      data-testid={`usage-${u.user_id}`}
                    >
                      <Zap className="w-3 h-3" />
                    </Button>
                  )}
                  {u.role === "candidate" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEmail(u)}
                      className="rounded-xl h-8 px-2 text-xs"
                      title="Manage application email (support)"
                      data-testid={`email-${u.user_id}`}
                    >
                      <Mail className="w-3 h-3" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={u.is_self || u.role === "admin"}
                    onClick={() => { setDeleteTarget(u); setDeleteConfirmText(""); }}
                    className="rounded-xl h-8 px-2 text-xs hover:bg-[#E11D48] hover:text-white hover:border-[#E11D48] disabled:opacity-30"
                    data-testid={`delete-${u.user_id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reset Password Dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) closeReset(); }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold">Reset password</DialogTitle>
            <DialogDescription>
              {resetTarget?.email}
            </DialogDescription>
          </DialogHeader>

          {!resetResult ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setResetMode("generate")}
                  className={`p-3 border text-sm font-semibold uppercase tracking-wider ${resetMode === "generate" ? "bg-[#0025D8] text-white border-[#0025D8]" : "bg-white border-border"}`}
                  data-testid="mode-generate"
                >Generate temp</button>
                <button
                  type="button"
                  onClick={() => setResetMode("manual")}
                  className={`p-3 border text-sm font-semibold uppercase tracking-wider ${resetMode === "manual" ? "bg-[#0025D8] text-white border-[#0025D8]" : "bg-white border-border"}`}
                  data-testid="mode-manual"
                >Set new password</button>
              </div>
              {resetMode === "manual" ? (
                <div>
                  <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">New password</Label>
                  <Input
                    type="text"
                    value={manualPassword}
                    onChange={(e) => setManualPassword(e.target.value)}
                    placeholder="min 6 characters"
                    className="rounded-xl py-6 font-mono"
                    data-testid="reset-manual-password"
                    autoFocus
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">A 12-character secure password will be generated. You'll see it once — copy it and share with the user.</p>
              )}
              <p className="text-xs text-muted-foreground">All active sessions for this user will be revoked.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-[#10B981] bg-[#10B981]/5 p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#10B981] mb-2">{resetResult.generated ? "Temporary password (shown once)" : "New password set"}</div>
                {resetResult.generated ? (
                  <div className="flex items-center justify-between gap-3">
                    <code className="font-mono text-xl font-bold break-all" data-testid="temp-password">{resetResult.temporary_password}</code>
                    <Button onClick={copyTempPassword} variant="outline" className="rounded-xl flex-shrink-0" data-testid="copy-temp-password">
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? "Copied" : "Copy"}
                    </Button>
                  </div>
                ) : (
                  <p className="font-mono text-sm">Password updated for <strong>{resetResult.email}</strong>. Share it with the user securely.</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">For security, this password will not be shown again.</p>
            </div>
          )}

          <DialogFooter>
            {!resetResult ? (
              <>
                <Button variant="outline" className="rounded-xl" onClick={closeReset}>Cancel</Button>
                <Button onClick={performReset} disabled={busy} className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3]" data-testid="confirm-reset">
                  {busy ? "Resetting…" : "Reset password"}
                </Button>
              </>
            ) : (
              <Button onClick={closeReset} className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3]">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage application email (managed inbox) Dialog — support: rotate the address + reset a blocked employer */}
      {/* Autopilot applications — the usage, the working, and the grant ledger in one place. */}
      <Dialog open={!!usageTarget} onOpenChange={(o) => { if (!o) { setUsageTarget(null); setUsage(null); } }}>
        <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-bold flex items-center gap-2">
              <Zap className="w-4 h-4" /> Autopilot applications
            </DialogTitle>
            <DialogDescription>
              {usageTarget?.email} — what they have used, and why each application did or didn’t count.
            </DialogDescription>
          </DialogHeader>

          {usageLoading && <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>}

          {usage && !usageLoading && (
            <div className="space-y-5">
              {/* The arithmetic, spelled out. A bare "2 of 50" is the number being disputed. */}
              <div className="rounded-xl border border-border bg-secondary/30 p-4">
                <div className="flex items-baseline gap-2 flex-wrap text-sm">
                  <span className="font-display font-bold text-2xl text-[#0F172A]">
                    {usage.quota.used}
                  </span>
                  <span className="text-muted-foreground">of</span>
                  <span className="font-display font-bold text-2xl text-[#0F172A]">
                    {usage.quota.limit}
                  </span>
                  <span className="text-muted-foreground">
                    used · {usage.quota.left} left ·{" "}
                    {usage.quota.window === "month" ? "resets on the 1st" : "one-time, never resets"}
                  </span>
                </div>
                <div className="mt-2 font-mono text-[11px] text-muted-foreground">
                  plan allowance {usage.quota.base}
                  {usage.quota.granted !== 0 && (
                    <span className="text-[#0025D8] font-semibold">
                      {" "}{usage.quota.granted > 0 ? "+" : "−"} {Math.abs(usage.quota.granted)} granted
                    </span>
                  )}
                  {" "}= {usage.quota.limit} · {usage.user.plan} · period {usage.quota.period_key}
                </div>
                <div className="mt-1 text-[12px] text-muted-foreground">
                  {usage.lifetime_total} application{usage.lifetime_total === 1 ? "" : "s"} all time,
                  of which <span className="font-semibold text-[#0F172A]">{usage.counted_now}</span> count
                  toward the current allowance.
                </div>
              </div>

              {/* Add or take back. Negative amounts are how a mistake is corrected. */}
              <div className="rounded-xl border border-border p-4">
                <div className="text-xs font-semibold text-[#0F172A] mb-2">Add applications</div>
                <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr_auto] gap-2">
                  <Input type="number" placeholder="e.g. 10" className="rounded-xl"
                    value={grantAmount} onChange={(e) => setGrantAmount(e.target.value)} />
                  <Input placeholder="Reason — shown in the ledger below" className="rounded-xl"
                    value={grantReason} onChange={(e) => setGrantReason(e.target.value)} />
                  <Button onClick={submitGrant} loading={granting} className="rounded-xl">Add</Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  A negative number takes applications back. Nothing is ever edited or deleted — each
                  change is a new line, so the history survives.
                  {usage.quota.window === "month"
                    ? " This grant applies to the current month only."
                    : " This grant is permanent (the free trial has no reset)."}
                </p>
              </div>

              {usage.grants.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-[#0F172A] mb-1.5">Grant history</div>
                  <ul className="rounded-xl border border-border divide-y divide-border overflow-hidden">
                    {usage.grants.map((g) => (
                      <li key={g.grant_id} className="flex items-start gap-3 px-3 py-2 text-[12px] bg-white">
                        <span className={`font-mono font-bold ${g.amount > 0 ? "text-[#047857]" : "text-[#E11D48]"}`}>
                          {g.amount > 0 ? "+" : "−"}{Math.abs(g.amount)}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="text-[#0F172A]">{g.reason || "no reason given"}</span>
                          <span className="block text-[11px] text-muted-foreground">
                            {g.admin_email || g.admin_user_id} · {(g.created_at || "").slice(0, 16).replace("T", " ")}
                            {g.period_key !== usage.quota.period_key && " · expired"}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <div className="text-xs font-semibold text-[#0F172A] mb-1.5">
                  Applications ({usage.applications.length})
                </div>
                <ul className="rounded-xl border border-border divide-y divide-border overflow-hidden max-h-[320px] overflow-y-auto">
                  {usage.applications.map((a) => (
                    <li key={a.draft_id} className={`flex items-center gap-3 px-3 py-2 text-[12px] ${
                      a.counted ? "bg-white" : "bg-[#0F172A]/[0.025]"}`}>
                      <span className="font-mono text-[11px] text-muted-foreground w-[74px] flex-shrink-0">
                        {(a.created_at || "").slice(0, 10)}
                      </span>
                      <span className="flex-1 min-w-0 truncate text-[#0F172A]">
                        {a.company || "—"}{a.title ? ` · ${a.title}` : ""}
                      </span>
                      <span className="font-mono text-[10px] text-muted-foreground w-[96px] flex-shrink-0">
                        {a.status}
                      </span>
                      <span className={`text-[10px] font-semibold flex-shrink-0 ${
                        a.counted ? "text-[#047857]" : "text-muted-foreground"}`}
                        title={a.why_not || "counts toward the current allowance"}>
                        {a.counted ? "counted" : "not counted"}
                      </span>
                    </li>
                  ))}
                </ul>
                {usage.applications.some((a) => !a.counted) && (
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Hover a “not counted” row for the reason. Failed applications are never charged —
                    the error was ours.
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" className="rounded-xl"
              onClick={() => { setUsageTarget(null); setUsage(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailTarget} onOpenChange={(o) => { if (!o) { setEmailTarget(null); setInbox(null); } }}>
        <DialogContent className="rounded-xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display font-bold flex items-center gap-2"><Mail className="w-4 h-4" /> Application email</DialogTitle>
            <DialogDescription>{emailTarget?.email} — candidates can't change this; support only.</DialogDescription>
          </DialogHeader>

          {inboxLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : !inbox?.address ? (
            <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
              No application inbox yet — this candidate hasn't finished Autopilot onboarding. It's assigned automatically when they do.
            </div>
          ) : (
            <div className="space-y-5">
              {/* current address */}
              <div>
                <Label className="uppercase tracking-wider text-[10px] font-semibold text-muted-foreground mb-1.5 block">Current address</Label>
                <div className="font-mono text-sm font-semibold text-[#0F172A] break-all">{inbox.address}</div>
                <div className="text-xs text-muted-foreground mt-1">Recruiter mail forwards to <b className="text-[#0F172A]">{inbox.real_email || "their login email"}</b>.</div>
                {inbox.aliases?.length > 0 && (
                  <div className="text-[11px] text-muted-foreground mt-1">Also still receiving at: {inbox.aliases.join(", ")}</div>
                )}
              </div>

              {/* change address */}
              <div className="rounded-lg border border-border p-3.5 space-y-3">
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Change the address</div>
                <Button onClick={() => changeEmail({ rotate: true }, "rotate")} disabled={!!emailBusy}
                  variant="outline" className="rounded-xl w-full justify-center gap-2">
                  {emailBusy === "rotate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />} Reset to a new address
                </Button>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground"><span className="flex-1 border-t border-border" />or set a specific one<span className="flex-1 border-t border-border" /></div>
                <div className="flex items-stretch gap-2">
                  <div className="flex-1 flex items-center rounded-xl border border-border overflow-hidden">
                    <input value={customLocal} onChange={(e) => setCustomLocal(e.target.value.replace(/[^a-z0-9._-]/gi, ""))}
                      placeholder="new.local-part" className="flex-1 px-3 py-2 text-sm font-mono focus:outline-none min-w-0" />
                    <span className="px-2 text-xs text-muted-foreground bg-muted/40 py-2 whitespace-nowrap">@{inbox.domain}</span>
                  </div>
                  <Button onClick={() => changeEmail({ address: `${customLocal}@${inbox.domain}` }, "save")}
                    disabled={!customLocal.trim() || !!emailBusy} className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3]">
                    {emailBusy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">The old address keeps forwarding as an alias. Existing employer accounts aren't renamed — reset the blocked one below.</p>
              </div>

              {/* employer accounts */}
              <div>
                <Label className="uppercase tracking-wider text-[10px] font-semibold text-muted-foreground mb-1.5 block">Employer accounts ({inbox.accounts?.length || 0})</Label>
                {(inbox.accounts || []).length === 0 ? (
                  <div className="text-xs text-muted-foreground">No saved employer accounts.</div>
                ) : (
                  <div className="space-y-1.5">
                    {inbox.accounts.map((a) => (
                      <div key={`${a.ats}:${a.tenant}`} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-[#0F172A] truncate">{a.company || a.tenant} <span className="font-normal text-muted-foreground">· {a.ats}</span></div>
                          <div className="font-mono text-[11px] text-muted-foreground truncate">{a.email}</div>
                        </div>
                        <button onClick={() => resetAccount(a)} disabled={!!emailBusy}
                          className="text-xs font-semibold px-2.5 py-1 rounded-lg border border-border hover:bg-[#E11D48]/10 hover:text-[#E11D48] hover:border-[#E11D48]/40 inline-flex items-center gap-1 flex-shrink-0">
                          {emailBusy === a.tenant ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Reset
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => { setEmailTarget(null); setInbox(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteConfirmText(""); } }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold text-[#E11D48]">Delete user permanently</DialogTitle>
            <DialogDescription className="text-foreground">
              You are about to delete <strong>{deleteTarget?.email}</strong>.
              This will also remove their <strong>{deleteTarget?.interview_count ?? 0} interview(s)</strong>
              {deleteTarget?.role === "company" && <> and <strong>{deleteTarget?.job_count ?? 0} job posting(s)</strong></>}
              . This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">
              Type <code className="text-[#E11D48] font-mono">{deleteTarget?.email}</code> to confirm
            </Label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="rounded-xl py-6 font-mono"
              data-testid="delete-confirm-input"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button
              onClick={performDelete}
              disabled={busy || deleteConfirmText !== deleteTarget?.email}
              className="rounded-xl bg-[#E11D48] hover:bg-[#BE123C] disabled:opacity-50"
              data-testid="confirm-delete"
            >
              <Trash2 className="w-4 h-4 mr-2" /> {busy ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { if (!o) closeCreate(); }}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display font-bold">Add new user</DialogTitle>
            <DialogDescription>Create an account on behalf of a user. Share the password with them securely.</DialogDescription>
          </DialogHeader>

          {!createResult ? (
            <div className="space-y-4">
              <div>
                <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Full name</Label>
                <Input
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="Jane Smith"
                  className="rounded-xl py-6"
                  data-testid="create-name"
                  autoFocus
                />
              </div>
              <div>
                <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Email</Label>
                <Input
                  type="email"
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  placeholder="jane@example.com"
                  className="rounded-xl py-6"
                  data-testid="create-email"
                />
              </div>
              <div>
                <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Role</Label>
                <Select value={createRole} onValueChange={setCreateRole}>
                  <SelectTrigger className="rounded-xl h-12 w-full" data-testid="create-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="candidate">Candidate</SelectItem>
                    <SelectItem value="company">Company</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="uppercase tracking-wider text-xs font-semibold mb-2 block">Password <span className="normal-case font-normal text-muted-foreground">(leave blank to auto-generate)</span></Label>
                <Input
                  type="text"
                  value={createPassword}
                  onChange={(e) => setCreatePassword(e.target.value)}
                  placeholder="min 6 characters"
                  className="rounded-xl py-6 font-mono"
                  data-testid="create-password"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-[#10B981] bg-[#10B981]/5 p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#10B981] mb-2">
                  {createResult.generated ? "Account created — temporary password (shown once)" : "Account created"}
                </div>
                <p className="text-sm mb-3">
                  <strong>{createResult.user.email}</strong> has been added as <strong>{createResult.user.role}</strong>.
                </p>
                {createResult.generated && (
                  <div className="flex items-center justify-between gap-3">
                    <code className="font-mono text-xl font-bold break-all" data-testid="create-temp-password">{createResult.temporary_password}</code>
                    <Button onClick={copyCreatePassword} variant="outline" className="rounded-xl flex-shrink-0" data-testid="copy-create-password">
                      {createCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {createCopied ? "Copied" : "Copy"}
                    </Button>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">For security, this password will not be shown again.</p>
            </div>
          )}

          <DialogFooter>
            {!createResult ? (
              <>
                <Button variant="outline" className="rounded-xl" onClick={closeCreate}>Cancel</Button>
                <Button
                  onClick={performCreate}
                  disabled={busy || !createName.trim() || !createEmail.trim()}
                  className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3]"
                  data-testid="confirm-create"
                >
                  <UserPlus className="w-4 h-4 mr-2" /> {busy ? "Creating…" : "Create user"}
                </Button>
              </>
            ) : (
              <Button onClick={closeCreate} className="rounded-xl bg-[#0025D8] hover:bg-[#001CB3]">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
